import PropTypes from 'prop-types'
import { AlertTriangle, CheckCircle, Info } from 'lucide-react'

/**
 * DataCompletenessIndicator Component
 *
 * Displays data completeness information to users, especially regarding
 * facilities with/without GPS coordinates.
 */
export default function DataCompletenessIndicator({ dataCompleteness }) {
  if (!dataCompleteness) {
    return null
  }

  const {
    totalFacilities,
    facilitiesWithCoordinates,
    missingCoordinates,
    coordinateCoveragePercent,
  } = dataCompleteness

  const isComplete = coordinateCoveragePercent === 100
  const isGood = coordinateCoveragePercent >= 95
  const isAcceptable = coordinateCoveragePercent >= 90

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="flex-shrink-0 mt-0.5">
          {isComplete ? (
            <CheckCircle className="w-5 h-5 text-green-600" />
          ) : isGood ? (
            <Info className="w-5 h-5 text-blue-600" />
          ) : (
            <AlertTriangle className="w-5 h-5 text-yellow-600" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1">
          <h4 className="font-semibold text-gray-900 mb-1">
            Data Completeness
          </h4>

          <div className="space-y-2 text-sm">
            {/* Summary */}
            <p className="text-gray-700">
              {isComplete ? (
                <span>
                  <span className="font-medium text-green-700">
                    Complete dataset:
                  </span>{' '}
                  All {totalFacilities} facilities have GPS coordinates.
                </span>
              ) : (
                <span>
                  <span className="font-medium">
                    {facilitiesWithCoordinates} of {totalFacilities} facilities
                  </span>{' '}
                  have GPS coordinates ({coordinateCoveragePercent.toFixed(1)}%
                  coverage).
                </span>
              )}
            </p>

            {/* Missing coordinates warning */}
            {!isComplete && (
              <div
                className={`px-3 py-2 rounded-lg ${
                  isGood
                    ? 'bg-blue-50 text-blue-800'
                    : isAcceptable
                      ? 'bg-yellow-50 text-yellow-800'
                      : 'bg-red-50 text-red-800'
                }`}
              >
                <p>
                  {missingCoordinates}{' '}
                  {missingCoordinates === 1 ? 'facility' : 'facilities'} cannot
                  be shown on maps due to missing GPS coordinates.
                  {isGood && ' This is normal - most data is available.'}
                </p>
              </div>
            )}

            {/* Visual progress bar */}
            <div className="mt-2">
              <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                <span>GPS Coverage</span>
                <span className="font-medium">
                  {coordinateCoveragePercent.toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${
                    isComplete
                      ? 'bg-green-600'
                      : isGood
                        ? 'bg-blue-600'
                        : isAcceptable
                          ? 'bg-yellow-600'
                          : 'bg-red-600'
                  }`}
                  style={{ width: `${coordinateCoveragePercent}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

DataCompletenessIndicator.propTypes = {
  dataCompleteness: PropTypes.shape({
    category: PropTypes.string.isRequired,
    totalFacilities: PropTypes.number.isRequired,
    facilitiesWithCoordinates: PropTypes.number.isRequired,
    missingCoordinates: PropTypes.number.isRequired,
    coordinateCoveragePercent: PropTypes.number.isRequired,
  }),
}
